import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, Eye, Heart } from 'lucide-react';
import { useNews } from '../contexts/NewsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import PageLayout from '../components/PageLayout';
import { BRAND } from '../styles/brand';

const NewsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { news, loading } = useNews();
  const { language } = useLanguage();
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';

  const getDateValue = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDate = (value) => {
    const date = getDateValue(value);
    if (!date) return '';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const sortedNews = [...news].sort((a, b) => {
    const aDate = getDateValue(a.publishedAt) || new Date(0);
    const bDate = getDateValue(b.publishedAt) || new Date(0);
    return bDate - aDate;
  });

  const selectedNews = id
    ? sortedNews.find((item) => String(item.id) === String(id))
    : null;

  if (loading) {
    return (
      <PageLayout title={t(language, 'news.title')} subtitle={t(language, 'news.subtitle')} compactHero>
        <p style={{ textAlign: 'center', color: BRAND.muted, padding: '2rem 0' }}>
          {t(language, 'news.loading')}
        </p>
      </PageLayout>
    );
  }

  if (selectedNews) {
    return (
      <PageLayout
        backTo="/news"
        backLabel={t(language, 'news.backToHome')}
        title={selectedNews.title}
        compactHero
      >
        <article className="app-card" style={{ overflow: 'hidden' }}>
          {selectedNews.imageUrl && (
            <img
              src={selectedNews.imageUrl}
              alt=""
              style={{ width: '100%', maxHeight: '360px', objectFit: 'cover', display: 'block' }}
            />
          )}
          <div style={{ padding: '1.5rem 1.35rem 1.75rem' }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1.25rem',
                color: BRAND.muted,
                marginBottom: '1.25rem',
                fontSize: '0.9rem'
              }}
            >
              {selectedNews.publishedAt && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Calendar size={16} aria-hidden />
                  {formatDate(selectedNews.publishedAt)}
                </span>
              )}
              {selectedNews.views !== undefined && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Eye size={16} aria-hidden />
                  {selectedNews.views}
                </span>
              )}
              {selectedNews.likes !== undefined && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Heart size={16} aria-hidden />
                  {selectedNews.likes}
                </span>
              )}
            </div>
            <p style={{ margin: 0, lineHeight: 1.75, color: BRAND.ink, fontSize: '1.05rem' }}>
              {selectedNews.content}
            </p>
          </div>
        </article>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t(language, 'news.title')}
      subtitle={t(language, 'news.subtitle')}
      tagline={language === 'fr' ? 'Actualités' : 'News'}
    >
      {sortedNews.length === 0 ? (
        <div className="app-card app-card--pad" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: BRAND.muted }}>{t(language, 'news.empty')}</p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}
        >
          {sortedNews.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(`/news/${item.id}`)}
              className="app-card"
              style={{
                padding: 0,
                textAlign: 'left',
                cursor: 'pointer',
                border: `1px solid ${BRAND.line}`,
                background: '#fff',
                overflow: 'hidden'
              }}
            >
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt=""
                  style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
                />
              )}
              <div style={{ padding: '1.1rem 1.15rem 1.25rem' }}>
                <div style={{ color: BRAND.muted, fontSize: '0.82rem', marginBottom: '0.4rem' }}>
                  {formatDate(item.publishedAt)}
                </div>
                <h2
                  style={{
                    margin: '0 0 0.5rem',
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.15rem',
                    color: BRAND.ink
                  }}
                >
                  {item.title}
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: BRAND.muted,
                    fontSize: '0.92rem',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {item.content}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default NewsPage;
